import { useNavigate } from 'react-router-dom'
import { lumi } from '../lib/lumi'
import {ArrowLeft, Loader, TrendingUp, Users} from 'lucide-react'
import { useState, useEffect } from 'react'

export default function FunilVendas() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [relatorio, setRelatorio] = useState('')
  const [propostas, setPropostas] = useState<any[]>([])

  useEffect(() => {
    carregarDados()
  }, [])

  const carregarDados = async () => {
    try {
      const user = lumi.auth.user
      const userId = user?.userId || user?.id || user?._id

      if (!userId) return

      const resultPropostas = await lumi.entities.propostas.list({
        filter: { creator: userId },
        sort: { createdAt: -1 },
        limit: 1000
      })

      setPropostas(resultPropostas?.list || [])
    } catch (error) {
      console.error('Erro ao carregar dados:', error)
    }
  }

  // Distribuição por status
  const propostasEnviadas = propostas.filter(p => p.status === 'criada')
  const propostasAceitas = propostas.filter(p => p.status === 'aceita')
  const propostasPerdidas = propostas.filter(p => p.status === 'perdida')

  const etapas = [
    { nome: 'Proposta Enviada', quantidade: propostasEnviadas.length, cor: 'bg-blue-500' },
    { nome: 'Venda Fechada', quantidade: propostasAceitas.length, cor: 'bg-green-500' },
    { nome: 'Perdido', quantidade: propostasPerdidas.length, cor: 'bg-red-500' }
  ]

  const totalPropostas = propostas.length
  const taxaFechamento = totalPropostas > 0 
    ? ((propostasAceitas.length / totalPropostas) * 100).toFixed(1)
    : '0.0'

  const gerarAnalise = async () => {
    setLoading(true)
    try {
      const response = await fetch('https://p410083387467890688.lumi.page/funil-vendas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ propostas })
      })

      if (!response.ok) throw new Error('Erro na requisição')
      
      const data = await response.json()
      setRelatorio(data.content)
    } catch (error) {
      console.error('Erro ao gerar análise:', error)
      alert('Erro ao gerar análise. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="p-3 hover:bg-gray-100 rounded-lg">
              <ArrowLeft size={24} className="text-gray-600" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-[#8B0000]">Funil de Vendas Visual</h1>
              <p className="text-sm text-gray-600">Visualize e otimize sua jornada de vendas</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Funil Visual */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-6 flex items-center gap-2">
            <TrendingUp size={22} className="text-[#8B0000]" />
            Visualização do Funil
          </h2>

          <div className="space-y-4">
            {etapas.map((etapa, index) => {
              const largura = totalPropostas > 0 
                ? (etapa.quantidade / totalPropostas * 100)
                : 0
              
              return (
                <div key={index} className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-900">{etapa.nome}</span>
                    <span className="text-sm text-gray-600">
                      {etapa.quantidade} ({totalPropostas > 0 ? ((etapa.quantidade / totalPropostas) * 100).toFixed(0) : 0}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-8">
                    <div
                      className={`${etapa.cor} h-8 rounded-full flex items-center justify-center text-white text-sm font-semibold transition-all duration-500`}
                      style={{ width: `${Math.max(largura, 5)}%` }}
                    >
                      {etapa.quantidade}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Taxa de Fechamento */}
          <div className="mt-6 p-4 bg-emerald-50 border border-emerald-200 rounded-lg">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-emerald-900">Taxa de Fechamento</span>
              <span className="text-2xl font-bold text-emerald-600">{taxaFechamento}%</span>
            </div>
          </div>
        </div>

        {/* Botão Gerar Análise */}
        <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center gap-3 mb-4">
            <Users size={24} className="text-[#8B0000]" />
            <div>
              <h2 className="text-lg font-bold text-gray-900">Análise Estratégica com IA</h2>
              <p className="text-sm text-gray-600">Receba recomendações personalizadas para seu funil</p>
            </div>
          </div>
          
          <button
            onClick={gerarAnalise}
            disabled={loading || propostas.length === 0}
            className="w-full bg-[#8B0000] hover:bg-[#6B0000] text-white px-6 py-4 rounded-lg font-semibold transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <Loader size={20} className="animate-spin" />
                Analisando funil...
              </>
            ) : (
              <>
                <TrendingUp size={20} />
                Gerar Análise Estratégica
              </>
            )}
          </button>

          {propostas.length === 0 && (
            <p className="text-sm text-gray-500 text-center mt-3">
              Crie propostas para visualizar seu funil de vendas
            </p>
          )}
        </div>

        {/* Relatório */}
        {relatorio && (
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
            <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp size={22} className="text-[#8B0000]" />
              Análise Estratégica do Funil
            </h2>
            <div className="prose prose-sm max-w-none">
              <div className="text-gray-700 whitespace-pre-wrap leading-relaxed">
                {relatorio}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  )
}
